import React from 'react';

interface GetEmployeeDataConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

type SearchBy = 'sender' | 'id' | 'email';

export const GetEmployeeDataConfig: React.FC<GetEmployeeDataConfigProps> = ({ config, onChange }) => {
  const searchBy: SearchBy =
    config.searchBy === 'id' ? 'id' : config.searchBy === 'email' ? 'email' : 'sender';
  const responseVariable = config.responseVariable ?? 'colaborador';
  const idVariable = config.idVariable ?? 'employee_id';

  const setSearchBy = (next: SearchBy) => {
    onChange('searchBy', next);
    // Provide a sensible default search value when switching modes
    if (next === 'email' && !String(config.searchValue || '').trim()) {
      onChange('searchValue', '{{email}}');
    } else if (next === 'id' && !String(config.searchValue || '').trim()) {
      onChange('searchValue', '{{employee_id}}');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Como funciona?</h4>
        <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside space-y-1">
          <li>Busca um <strong>colaborador</strong> (usuário do Bitrix24) e preenche os dados em variáveis.</li>
          <li>Se encontrar, segue pela saída <span className="font-medium text-green-600 dark:text-green-400">Encontrado</span>; senão por <span className="font-medium text-red-600 dark:text-red-400">Não encontrado</span>.</li>
          <li>Preenche: <code>{'{{employee_name}}'}</code>, <code>{'{{employee_email}}'}</code>, <code>{'{{employee_position}}'}</code>, <code>{'{{employee_department}}'}</code>, <code>{'{{employee_phone}}'}</code>, <code>{'{{employee_active}}'}</code>, <code>{'{{employee_id}}'}</code>.</li>
        </ul>
      </div>

      {/* Identificar por */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Identificar colaborador por</label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={searchBy}
          onChange={(e) => setSearchBy(e.target.value as SearchBy)}
        >
          <option value="sender">Remetente da conversa</option>
          <option value="email">E-mail</option>
          <option value="id">ID do Bitrix</option>
        </select>
        {searchBy === 'sender' && (
          <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
            Usa o usuário que enviou a mensagem (ideal para chat interno de colaboradores).
          </p>
        )}
      </div>

      {/* Valor para busca (id/email) */}
      {searchBy !== 'sender' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
            {searchBy === 'email' ? 'E-mail do colaborador' : 'ID do colaborador'}
          </label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={config.searchValue || ''}
            onChange={(e) => onChange('searchValue', e.target.value)}
            placeholder={searchBy === 'email' ? '{{email}}' : '{{employee_id}}'}
          />
        </div>
      )}

      {/* Variável do objeto completo */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Variável de saída (objeto completo)</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={responseVariable}
          onChange={(e) => onChange('responseVariable', e.target.value)}
          placeholder="colaborador"
        />
        <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
          Ex.: <code>{'{{colaborador.WORK_POSITION}}'}</code>, <code>{'{{colaborador.EMAIL}}'}</code>.
        </p>
      </div>

      {/* Variável do ID */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Variável do ID</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={idVariable}
          onChange={(e) => onChange('idVariable', e.target.value)}
          placeholder="employee_id"
        />
      </div>

      {/* Campos extras */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Campos extras <span className="text-slate-400 dark:text-slate-500 font-normal">(opcional)</span>
        </label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={Array.isArray(config.fetchFieldIds) ? config.fetchFieldIds.join(', ') : ''}
          onChange={(e) =>
            onChange(
              'fetchFieldIds',
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          placeholder="UF_USR_RAMAL, UF_DEPARTMENT"
        />
        <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
          IDs de campos do usuário separados por vírgula. Cada um vira uma variável com o mesmo nome.
        </p>
      </div>
    </div>
  );
};
